import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUserPlan, PLAN_CONFIG, CREDIT_TOP_UPS, CREDIT_COSTS, PlanType } from '../../hooks/useUserPlan';
import { useCredits } from '../../hooks/useCredits';
import { getEdgeFunctionHeaders } from '../../lib/edgeFunctions';

const PLAN_DETAILS = {
  free: {
    label: 'Free Plan',
    price: '$0',
    priceLabel: 'Free forever',
    color: 'text-stone-600',
    bgLight: 'bg-stone-50',
    border: 'border-stone-200',
    ring: 'ring-2 ring-stone-300',
    badge: 'bg-stone-100 text-stone-600',
    features: ['25 credits / month', '1 Business Profile', 'Facebook, Instagram, Google ads', '1 creative image per generation', 'Basic copy formats'],
  },
  pro: {
    label: 'Pro Plan',
    price: '$29',
    priceLabel: '/ month',
    color: 'text-orange-500',
    bgLight: 'bg-orange-50/60',
    border: 'border-orange-200',
    ring: 'ring-2 ring-orange-300',
    badge: 'bg-orange-100 text-orange-600',
    features: ['250 credits / month', 'Up to 3 Business Profiles', 'All platforms supported', '3 creative images per generation', 'Priority support', 'Ad angle & strategy controls'],
  },
  agency: {
    label: 'Agency Plan',
    price: '$99',
    priceLabel: '/ month',
    color: 'text-amber-600',
    bgLight: 'bg-amber-50/60',
    border: 'border-amber-200',
    ring: 'ring-2 ring-amber-300',
    badge: 'bg-amber-100 text-amber-700',
    features: ['1,000 credits / month', 'Unlimited Business Profiles', 'Client workspace support', '6 creative images per generation', 'White-label + API access', 'Dedicated account support'],
  },
};

const PLAN_ORDER: PlanType[] = ['free', 'pro', 'agency'];
const PLAN_RANK: Record<PlanType, number> = { free: 0, pro: 1, agency: 2 };
const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-subscription`;

const ACTION_META: Record<string, { label: string; icon: string; iconColor: string; bgColor: string }> = {
  generate_ad:      { label: 'Ad Generation',       icon: 'ri-magic-line',         iconColor: 'text-orange-500', bgColor: 'bg-orange-50' },
  regenerate_image: { label: 'Image Regeneration',  icon: 'ri-refresh-line',       iconColor: 'text-amber-500',  bgColor: 'bg-amber-50' },
  edit_image:       { label: 'Image Edit',           icon: 'ri-edit-line',          iconColor: 'text-stone-500',  bgColor: 'bg-stone-100' },
  monthly_reset:    { label: 'Monthly Reset',        icon: 'ri-refresh-fill',       iconColor: 'text-green-500',  bgColor: 'bg-green-50' },
  top_up:           { label: 'Credit Top-Up',        icon: 'ri-add-circle-line',    iconColor: 'text-green-500',  bgColor: 'bg-green-50' },
  plan_upgrade:     { label: 'Plan Upgrade Bonus',   icon: 'ri-vip-crown-line',     iconColor: 'text-orange-500', bgColor: 'bg-orange-50' },
};

const PURCHASE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/purchase-credits`;

export default function BillingPage() {
  const { user } = useAuth();
  const { plan } = useUserPlan();
  const {
    creditsRemaining,
    creditLimit,
    creditPercent,
    creditsResetAt,
    daysUntilReset,
    pendingPlan,
    loading: creditsLoading,
    recentTransactions,
    refetch,
  } = useCredits();

  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState<'overview' | 'plan' | 'history'>('overview');
  const [confirmPlan, setConfirmPlan] = useState<PlanType | null>(null);
  const [planSwitching, setPlanSwitching] = useState(false);
  const [planMsg, setPlanMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [topUpLoading, setTopUpLoading] = useState<string | null>(null);
  const [topUpMsg, setTopUpMsg] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  // Handle return from Stripe checkout
  useEffect(() => {
    const topup = searchParams.get('topup');
    const pack = searchParams.get('pack');
    if (topup === 'success') {
      const packLabel = CREDIT_TOP_UPS.find(p => p.id === pack)?.label ?? 'Credit pack';
      setTopUpMsg({ type: 'success', text: `Payment successful! Your ${packLabel} credits are being added to your account — refresh in a moment if they haven't appeared yet.` });
      setSearchParams({}, { replace: true });
      refetch();
    } else if (topup === 'cancelled') {
      setTopUpMsg({ type: 'info', text: 'Checkout cancelled — no charge was made.' });
      setSearchParams({}, { replace: true });
    }
  }, [refetch, searchParams, setSearchParams]);

  const handleTopUp = async (packId: string) => {
    if (!user) return;
    setTopUpLoading(packId);
    setTopUpMsg(null);
    try {
      const res = await fetch(PURCHASE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ packId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not start checkout.');
      // Redirect to Stripe — external URL so window.location is required
      window.location.href = json.url;
    } catch (err: unknown) {
      setTopUpMsg({ type: 'error', text: err instanceof Error ? err.message : 'Could not process purchase.' });
      setTopUpLoading(null);
    }
  };

  const handlePlanChange = async (targetPlan: PlanType) => {
    if (!user) return;
    const isUpgrade = PLAN_RANK[targetPlan] > PLAN_RANK[plan as PlanType];
    const action = isUpgrade ? 'upgrade' : 'downgrade';
    setPlanSwitching(true);
    setPlanMsg(null);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ action, newPlan: targetPlan }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Something went wrong.');
      setPlanMsg({ type: isUpgrade ? 'success' : 'info', text: json.message });
      setConfirmPlan(null);
      await refetch();
    } catch (err: unknown) {
      setPlanMsg({ type: 'error', text: err instanceof Error ? err.message : 'Could not process plan change.' });
    } finally {
      setPlanSwitching(false);
    }
  };

  const handleCancelPending = async () => {
    if (!user) return;
    setCancellingPending(true);
    setPlanMsg(null);
    try {
      const res = await fetch(EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({ action: 'cancel_pending' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? 'Could not cancel.');
      setPlanMsg({ type: 'success', text: json.message });
      await refetch();
    } catch (err: unknown) {
      setPlanMsg({ type: 'error', text: err instanceof Error ? err.message : 'Could not cancel plan change.' });
    } finally {
      setCancellingPending(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'ri-home-4-line' },
    { id: 'plan', label: 'Plans', icon: 'ri-vip-crown-line' },
    { id: 'history', label: 'History', icon: 'ri-receipt-line' },
  ] as const;

  const planInfo = PLAN_DETAILS[plan as keyof typeof PLAN_DETAILS] ?? PLAN_DETAILS.free;

  const resetDateFormatted = creditsResetAt
    ? new Date(creditsResetAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';

  const resetDateShort = creditsResetAt
    ? new Date(creditsResetAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const barColor = creditPercent > 50
    ? 'bg-gradient-to-r from-orange-400 to-amber-400'
    : creditPercent > 20
    ? 'bg-gradient-to-r from-amber-400 to-yellow-400'
    : 'bg-gradient-to-r from-red-400 to-rose-400';

  return (
    <div className="flex-1 overflow-y-auto bg-stone-50 min-h-screen">
      {/* Header */}
      <header className="h-16 bg-white border-b border-stone-100 flex items-center justify-between px-6 shrink-0 sticky top-0 z-10">
        <div>
          <h1 className="text-base font-bold text-stone-900">Billing</h1>
          <p className="text-xs text-stone-400">Manage your plan, credits, and payment details</p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold ${planInfo.badge}`}>
          <i className="ri-vip-crown-line" />
          {planInfo.label}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-6">

        {/* Tab switcher */}
        <div className="flex gap-1 bg-stone-100 rounded-xl p-1 w-fit">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeSection === tab.id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Plan feedback message */}
        {planMsg && (
          <div className={`flex items-start gap-3 px-5 py-4 rounded-2xl border text-sm ${
            planMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800'
            : planMsg.type === 'info'  ? 'bg-stone-50 border-stone-200 text-stone-700'
            : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <i className={`text-base shrink-0 mt-0.5 ${
              planMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500'
              : planMsg.type === 'info'  ? 'ri-information-line text-stone-400'
              : 'ri-error-warning-line text-red-500'
            }`} />
            <span>{planMsg.text}</span>
          </div>
        )}

        {/* Top-up feedback message */}
        {topUpMsg && (
          <div className={`flex items-start gap-3 px-5 py-4 rounded-2xl border text-sm ${
            topUpMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-800'
            : topUpMsg.type === 'info'  ? 'bg-stone-50 border-stone-200 text-stone-700'
            : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <i className={`text-base shrink-0 mt-0.5 ${
              topUpMsg.type === 'success' ? 'ri-checkbox-circle-line text-green-500'
              : topUpMsg.type === 'info'  ? 'ri-information-line text-stone-400'
              : 'ri-error-warning-line text-red-500'
            }`} />
            <span>{topUpMsg.text}</span>
            <button onClick={() => setTopUpMsg(null)} className="ml-auto shrink-0 text-stone-400 hover:text-stone-600 cursor-pointer">
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* Pending downgrade banner */}
        {pendingPlan && (
          <div className="flex items-start justify-between gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-calendar-schedule-line text-amber-500 text-lg" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-800">Scheduled plan change</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  You&apos;ll move to the <span className="font-bold capitalize">{PLAN_DETAILS[pendingPlan as keyof typeof PLAN_DETAILS]?.label ?? pendingPlan}</span> on <span className="font-bold">{resetDateFormatted}</span>.
                  You keep all current features and credits until then.
                </p>
              </div>
            </div>
            <button
              onClick={handleCancelPending}
              disabled={cancellingPending}
              className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              {cancellingPending ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-close-circle-line" />}
              Cancel change
            </button>
          </div>
        )}

        {/* ═══════════════ OVERVIEW TAB ═══════════════ */}
        {activeSection === 'overview' && (
          <div className="space-y-5">

            {/* Summary stat row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Active Plan */}
              <div className={`rounded-2xl border p-5 ${planInfo.bgLight} ${planInfo.border}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/80">
                    <i className="ri-vip-crown-line text-sm text-stone-600" />
                  </div>
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Active Plan</span>
                </div>
                <div className={`text-xl font-bold ${planInfo.color}`}>{planInfo.label}</div>
                <div className="text-xs text-stone-500 mt-1">{planInfo.price}{plan !== 'free' ? ' / month' : ' — Free forever'}</div>
              </div>

              {/* Next Renewal */}
              <div className="rounded-2xl border border-stone-100 bg-white p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-50">
                    <i className="ri-calendar-check-line text-sm text-stone-600" />
                  </div>
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Credits Renew</span>
                </div>
                <div className="text-xl font-bold text-stone-900">{resetDateShort}</div>
                <div className="text-xs text-stone-500 mt-1">{daysUntilReset} days from now</div>
              </div>

              {/* Credits Remaining */}
              <div className="rounded-2xl border border-stone-100 bg-white p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-50">
                    <i className="ri-copper-coin-line text-sm text-stone-600" />
                  </div>
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Credits Left</span>
                </div>
                <div className="text-xl font-bold text-stone-900">
                  {creditsLoading ? '—' : creditsRemaining.toLocaleString()}
                  <span className="text-sm font-normal text-stone-400"> / {creditLimit.toLocaleString()}</span>
                </div>
                <div className="mt-2 w-full h-1.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${creditPercent}%` }} />
                </div>
              </div>
            </div>

            {/* Current Plan Detail */}
            <div className={`rounded-2xl border p-6 ${plan !== 'free' ? planInfo.bgLight + ' ' + planInfo.border : 'bg-white border-stone-100'}`}>
              <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
                <div>
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Your Subscription</span>
                  <h2 className="text-2xl font-bold text-stone-900 mt-0.5">{planInfo.label}</h2>
                  <p className="text-sm text-stone-500 mt-1">
                    {plan === 'free'
                      ? 'Free forever — no payment required'
                      : <>Next charge of <span className="font-semibold text-stone-800">{planInfo.price}</span> on <span className="font-semibold text-stone-800">{resetDateShort}</span></>}
                  </p>
                </div>
                {plan !== 'agency' && (
                  <button
                    onClick={() => setActiveSection('plan')}
                    className="flex items-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-up-circle-line" />
                    Upgrade Plan
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {planInfo.features.map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-stone-700">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-check-line text-orange-400 text-sm" />
                    </div>
                    {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-stone-900">Payment Method</h2>
                <span className="text-xs text-stone-400 bg-stone-50 px-2.5 py-1 rounded-full border border-stone-100">Stripe secured</span>
              </div>
              <div className="flex items-center justify-between py-4 px-4 rounded-xl border border-dashed border-stone-200 bg-stone-50/50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-stone-200/60">
                    <i className="ri-bank-card-line text-stone-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-600">No payment method on file</p>
                    <p className="text-xs text-stone-400 mt-0.5">A card is saved automatically when you complete a purchase</p>
                  </div>
                </div>
              </div>
              <p className="text-xs text-stone-400 mt-3 flex items-center gap-1.5">
                <i className="ri-secure-payment-line" />
                Payments processed securely via Stripe — your card details are never stored here
              </p>
            </div>

            {/* Credit Cost Reference */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h2 className="text-sm font-bold text-stone-900 mb-4">Credit Cost Reference</h2>
              <div className="divide-y divide-stone-50">
                {[
                  { icon: 'ri-magic-line', bg: 'bg-orange-50', color: 'text-orange-500', label: 'Generate Ad Campaign', sub: 'Standard: 1 image = 10, 3 images = 27, 6 images = 48', cost: 'from 10 credits' },
                  { icon: 'ri-sparkling-2-line', bg: 'bg-sky-50', color: 'text-sky-500', label: 'Social Post', sub: 'Captions, hooks, CTAs, hashtags, and content angle', cost: '3 credits' },
                  { icon: 'ri-image-line', bg: 'bg-cyan-50', color: 'text-cyan-600', label: 'Social Post Image', sub: 'Optional AI image added to a social post', cost: '+5 credits' },
                  { icon: 'ri-movie-line', bg: 'bg-rose-50', color: 'text-rose-500', label: 'Video Ads', sub: '5 sec = 50, 10 sec = 100, voice/music extra', cost: 'from 50 credits' },
                  { icon: 'ri-refresh-line', bg: 'bg-amber-50', color: 'text-amber-500', label: 'Re-generate / Variation', sub: 'Swap one creative or generate a variation', cost: `${CREDIT_COSTS.REGENERATE_IMAGE} credits` },
                  { icon: 'ri-edit-line', bg: 'bg-stone-100', color: 'text-stone-500', label: 'Rewrite Ad Copy', sub: 'Refresh the copy without changing images', cost: `${CREDIT_COSTS.REWRITE_COPY} credits` },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${item.bg} shrink-0`}>
                        <i className={`${item.icon} text-sm ${item.color}`} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-stone-800">{item.label}</div>
                        <div className="text-xs text-stone-400">{item.sub}</div>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-stone-600 whitespace-nowrap">{item.cost}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Credit Top-Up Packs ── */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold text-stone-900">Credit Top-Up Packs</h2>
                <span className="text-xs text-stone-400 bg-stone-50 px-2.5 py-1 rounded-full border border-stone-100">Credits never expire</span>
              </div>
              <p className="text-xs text-stone-400 mb-5">Need more credits before your reset? Buy a one-time pack — these stack on top of your monthly allowance and never expire.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {CREDIT_TOP_UPS.map(pack => {
                  const isLoading = topUpLoading === pack.id;
                  const isPopular = pack.id === 'growth';
                  return (
                    <div
                      key={pack.id}
                      className={`relative rounded-xl border p-5 flex flex-col gap-3 transition-all duration-200 ${
                        isPopular ? 'border-orange-300 bg-orange-50/60' : 'border-stone-200 bg-stone-50/40 hover:border-stone-300'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-400 text-white text-[10px] font-bold px-3 py-0.5 rounded-full whitespace-nowrap">
                          Most Popular
                        </div>
                      )}
                      {pack.saving && (
                        <span className="absolute top-3 right-3 text-[10px] font-bold text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                          {pack.saving}
                        </span>
                      )}
                      <div>
                        <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">{pack.label}</div>
                        <div className="text-2xl font-bold text-stone-900">{pack.credits.toLocaleString()}</div>
                        <div className="text-xs text-stone-400">credits</div>
                      </div>
                      <div className="text-lg font-bold text-stone-800">{pack.price} <span className="text-sm font-normal text-stone-400">one-time</span></div>
                      <button
                        onClick={() => handleTopUp(pack.id)}
                        disabled={topUpLoading !== null}
                        className={`w-full flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                          isLoading
                            ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                            : isPopular
                            ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                            : 'bg-stone-800 text-white hover:bg-stone-900'
                        }`}
                      >
                        {isLoading
                          ? <><i className="ri-loader-4-line animate-spin" /> Processing...</>
                          : <><i className="ri-shopping-cart-line" /> Buy {pack.label}</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        )}

        {/* ═══════════════ PLAN TAB ═══════════════ */}
        {activeSection === 'plan' && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-sm font-bold text-stone-900">Choose Your Plan</h2>
                <span className="text-xs text-stone-400 bg-stone-50 px-3 py-1 rounded-full border border-stone-100">Billing date stays the same</span>
              </div>
              <p className="text-xs text-stone-400 mb-6">
                <span className="font-semibold text-stone-600">Upgrades</span> take effect immediately — prorated charge + credits reset to your new allowance.&nbsp;
                <span className="font-semibold text-stone-600">Downgrades</span> are scheduled for your next billing cycle on {resetDateShort}.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {PLAN_ORDER.map(tier => {
                  const info = PLAN_DETAILS[tier];
                  const isCurrent = tier === plan;
                  const isPending = tier === pendingPlan;
                  const isUpgrade = PLAN_RANK[tier] > PLAN_RANK[plan as PlanType];
                  const isDowngrade = PLAN_RANK[tier] < PLAN_RANK[plan as PlanType];
                  const isConfirming = confirmPlan === tier;

                  return (
                    <div
                      key={tier}
                      className={`relative rounded-2xl border p-5 flex flex-col gap-4 transition-all duration-200 ${
                        isCurrent
                          ? info.ring + ' ' + info.bgLight + ' ' + info.border
                          : isPending
                          ? 'border-amber-300 bg-amber-50/40'
                          : isConfirming
                          ? 'border-orange-300 bg-orange-50/20'
                          : 'border-stone-200 hover:border-stone-300 bg-white'
                      }`}
                    >
                      {isCurrent && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                          Current plan
                        </div>
                      )}
                      {isPending && !isCurrent && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">
                          Scheduled
                        </div>
                      )}

                      <div>
                        <span className={`inline-block text-[10px] font-bold px-2.5 py-1 rounded-full mb-2 ${info.badge}`}>
                          {info.label}
                        </span>
                        <div className="flex items-end gap-1">
                          <span className={`text-3xl font-bold ${isCurrent ? info.color : 'text-stone-900'}`}>{info.price}</span>
                          <span className="text-sm text-stone-400 mb-0.5">{info.priceLabel}</span>
                        </div>
                        <div className="text-xs text-stone-400 mt-0.5">{PLAN_CONFIG[tier].creditLimit.toLocaleString()} credits / month</div>
                      </div>

                      <ul className="space-y-2 flex-1">
                        {info.features.map(f => (
                          <li key={f} className="flex items-start gap-2 text-xs text-stone-600">
                            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                              <i className={`ri-check-line ${isCurrent ? info.color : 'text-stone-400'} text-sm`} />
                            </div>
                            {f}
                          </li>
                        ))}
                      </ul>

                      <div className="pt-3 border-t border-stone-100/80">
                        {isCurrent ? (
                          <div className="flex items-center gap-1.5 text-xs text-stone-400 font-medium">
                            <i className="ri-shield-check-line" />
                            Active now
                          </div>
                        ) : isPending ? (
                          <div className="flex items-center gap-1.5 text-xs text-amber-600 font-semibold">
                            <i className="ri-time-line" />
                            Takes effect {resetDateShort}
                          </div>
                        ) : isConfirming ? (
                          <div className="space-y-2">
                            <p className="text-[11px] text-stone-600 leading-snug">
                              {isUpgrade
                                ? `Upgrade now? You'll be charged a prorated amount today and get ${PLAN_CONFIG[tier].creditLimit.toLocaleString()} fresh credits immediately.`
                                : `Downgrade on ${resetDateShort}? You keep all current features until then.`}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePlanChange(tier)}
                                disabled={planSwitching}
                                className={`flex-1 flex items-center justify-center gap-1 text-xs font-bold py-2 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${
                                  isUpgrade
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                                    : 'bg-stone-700 text-white hover:bg-stone-800'
                                }`}
                              >
                                {planSwitching ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-check-line" />}
                                Confirm
                              </button>
                              <button
                                onClick={() => setConfirmPlan(null)}
                                disabled={planSwitching}
                                className="flex-1 flex items-center justify-center text-xs font-semibold py-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-all duration-200 cursor-pointer whitespace-nowrap"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setConfirmPlan(tier); setPlanMsg(null); }}
                            className={`w-full flex items-center justify-center gap-1.5 text-xs font-bold py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap ${
                              isUpgrade
                                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                                : 'border border-stone-300 text-stone-600 bg-white hover:bg-stone-50'
                            }`}
                          >
                            {isUpgrade ? (
                              <><i className="ri-arrow-up-circle-line" /> Upgrade — charged today</>
                            ) : (
                              <><i className="ri-arrow-down-circle-line" /> Switch on {resetDateShort}</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Compare plans link */}
            <div className="text-center">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-600 transition-colors duration-150 cursor-pointer"
              >
                <i className="ri-external-link-line" />
                View full plan comparison on pricing page
              </Link>
            </div>
          </div>
        )}

        {/* ═══════════════ HISTORY TAB ═══════════════ */}
        {activeSection === 'history' && (
          <div className="space-y-5">

            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-2xl border border-stone-100 p-5">
                <div className="text-xs text-stone-500 uppercase tracking-wider font-semibold mb-2">Credits Used</div>
                <div className="text-3xl font-bold text-stone-900">
                  {creditsLoading ? '—' : (creditLimit - creditsRemaining).toLocaleString()}
                </div>
                <div className="text-xs text-stone-400 mt-1">out of {creditLimit.toLocaleString()} this period</div>
              </div>
              <div className="bg-white rounded-2xl border border-stone-100 p-5">
                <div className="text-xs text-stone-500 uppercase tracking-wider font-semibold mb-2">Credits Remaining</div>
                <div className="text-3xl font-bold text-stone-900">
                  {creditsLoading ? '—' : creditsRemaining.toLocaleString()}
                </div>
                <div className="text-xs text-stone-400 mt-1">resets {resetDateShort}</div>
              </div>
              <div className="bg-white rounded-2xl border border-stone-100 p-5">
                <div className="text-xs text-stone-500 uppercase tracking-wider font-semibold mb-2">Transactions</div>
                <div className="text-3xl font-bold text-stone-900">{recentTransactions.length}</div>
                <div className="text-xs text-stone-400 mt-1">shown below (last 10)</div>
              </div>
            </div>

            {/* Transaction list */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-bold text-stone-900">Transaction History</h2>
                <span className="text-xs text-stone-400">Last 10 transactions</span>
              </div>

              {creditsLoading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-stone-400 py-12">
                  <i className="ri-loader-4-line animate-spin" />
                  Loading transactions...
                </div>
              ) : recentTransactions.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-16 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-stone-50 text-stone-300 text-2xl">
                    <i className="ri-receipt-line" />
                  </div>
                  <p className="text-sm font-semibold text-stone-500">No transactions yet</p>
                  <p className="text-xs text-stone-400 max-w-xs">Start generating ads and your full credit usage history will appear here.</p>
                  <Link
                    to="/dashboard/generate"
                    className="flex items-center gap-1.5 text-xs font-bold px-4 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap mt-1"
                  >
                    <i className="ri-flashlight-fill" />
                    Generate Your First Ad
                  </Link>
                </div>
              ) : (
                <div className="space-y-0">
                  {/* Table header */}
                  <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 px-3 py-2 mb-1">
                    <div className="w-8" />
                    <div className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Description</div>
                    <div className="text-xs font-semibold text-stone-400 uppercase tracking-wider text-right">Date</div>
                    <div className="text-xs font-semibold text-stone-400 uppercase tracking-wider text-right w-16">Credits</div>
                  </div>

                  {recentTransactions.map((tx, idx) => {
                    const meta = ACTION_META[tx.action_type] ?? { label: tx.action_type, icon: 'ri-coin-line', iconColor: 'text-stone-500', bgColor: 'bg-stone-100' };
                    const isPositive = tx.amount > 0;
                    const date = new Date(tx.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const time = new Date(tx.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div
                        key={tx.id}
                        className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-3 py-3.5 rounded-xl transition-colors duration-150 hover:bg-stone-50 ${idx !== recentTransactions.length - 1 ? 'border-b border-stone-50' : ''}`}
                      >
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${meta.bgColor}`}>
                          <i className={`${meta.icon} text-sm ${meta.iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-stone-800 truncate">{meta.label}</div>
                          <div className="text-xs text-stone-400 truncate">{tx.description ?? meta.label}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs font-medium text-stone-700">{date}</div>
                          <div className="text-xs text-stone-400">{time}</div>
                        </div>
                        <div className={`text-sm font-bold text-right w-16 shrink-0 ${isPositive ? 'text-green-500' : 'text-stone-700'}`}>
                          {isPositive ? '+' : ''}{tx.amount}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

